'use client';

import cn from 'classnames';
import { Wrapper } from 'src/components/Wrapper';
import { useIntersectionObserveForms } from 'src/hooks/useIntersectionObserverForms';
import { Layout } from 'src/layout';
import { securityMock, type TableSecurityProps } from 'src/mocks/security';
import { type Catalog } from 'src/types/backendContent';
import { Accordion } from 'src/uikit/Accordion';
import { Benefits } from 'src/uikit/Benefits';
import { Variant } from 'src/uikit/Button/constants';
import { Form } from 'src/uikit/Form';
import { FullScreen } from 'src/uikit/FullScreen';
import { HeroInnerPage } from 'src/uikit/HeroInnerPage';
import { Link } from 'src/uikit/Link';
import { ProductsList } from 'src/uikit/ProductsList';
import { scrollToElementWithId } from 'src/utils/scrollToElementWithId';

import { About } from '../../uikit/About';
import s from './Security.module.scss';

function Table({ data }: { data: TableSecurityProps }) {
  const footnoteId = 'table-footnote-link';
  const footnoteLink =
    'https://cdn.cloud.ru/backend/docs/contracts/Prilozheniye_%E2%84%962.0.pdf';
  const footnoteLinkText = '«Соглашения об SLA»';

  return (
    <section className={s.tableOverflow}>
      <Wrapper className={s.tableWrapper}>
        <div className={s.titleDescriptionHeader}>
          <h2 className={s.titleDescriptionTitle}>{data.head.title}</h2>
          <p className={s.titleDescriptionDescription}>{data.head.desc}</p>
        </div>
        <div className={s.table}>
          <div className={cn(s.tableSubhead, s.tableGrids)}>
            <p className={s.tableSubheadTitle} />
            <p className={s.tableSubheadTitle}>{data.subhead.titlePrivate}</p>
            <p className={s.tableSubheadTitle}>{data.subhead.titlePublic}</p>
          </div>
          <div className={cn(s.tableContent, s.tableGrids)}>
            <div />
            <div />
            <div className={s.tableContentColLow}>
              {data.colHeader.map((header, index) => {
                return (
                  <span className={s.tableContentColHeader} key={index}>
                    {header}
                  </span>
                );
              })}
            </div>
          </div>
          {data.colContent.map((col, index) => {
            return (
              <div className={cn(s.tableContent, s.tableGrids)} key={index}>
                <div className={s.tableContentCol}>
                  <p className={s.tableContentColTitle}>{col.title}</p>
                  <p className={s.tableContentColDesc}>{col.desc}</p>
                </div>
                <div className={cn(s.tableContentCol)}>
                  <span
                    className={cn(s.tableContentColText, col.clrTextPrivate)}
                  >
                    {col.textPrivate}
                  </span>
                </div>
                <div className={cn(s.tableContentCol)}>
                  <div className={s.tableContentColLow}>
                    <span
                      className={cn(s.tableContentColText, col.clrTextIaaS)}
                    >
                      {col.textIaaS}
                    </span>
                    <span
                      className={cn(s.tableContentColText, col.clrTextPaaS)}
                    >
                      {col.textPaaS}
                    </span>
                    <span
                      className={cn(s.tableContentColText, col.clrTextSaaS)}
                    >
                      {col.textSaaS}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
          <span className={s.footnote}>
            <p>
              * Cloud.ru отвечает за доступность консолей управления облаком,
              включая бекапы и т.д., но не отвечает за доступность IP адресов,
              выделенных клиентам, с помощью которых они публикуются в сеть
              интернет (белый IP адрес, API, Web серверы).
            </p>
            <p>
              Согласно п.3.4&nbsp;
              <Link
                href={footnoteLink}
                id={footnoteId}
                analyticsOptions={{
                  action: footnoteLink,
                  clickZone: 'body',
                  clickElement: 'textclick',
                  clickContent: footnoteLinkText,
                  uniqueId: footnoteId,
                  transitionType: 'outside-link',
                }}
              >
                {footnoteLinkText}
              </Link>
              &nbsp;стороны соглашаются учитывать периоды недоступности услуг и
              (или) деградации как предоставление услуг в штатном режиме и не
              учитывать их при расчете времени недоступности услуг, если:
            </p>
            <p>
              3.4.6. Недоступность услуг возникла в результате совершения
              третьими лицами противоправных действий, в том числе в случае
              проведения DoS/DDoS-атак на вычислительные системы заказчика;
            </p>
            <p>
              3.4.7. Нарушение доступности услуг возникло из-за DoS/DDoS-атаки
              на вычислительные системы заказчика при отсутствии подключения
              заказчиком услуги «Защита от DDoS-атак».
            </p>
          </span>
        </div>
      </Wrapper>
    </section>
  );
}

export function SecurityPage({
  data,
  tableData,
  catalog,
}: {
  data: typeof securityMock;
  tableData: TableSecurityProps;
  catalog: Catalog;
}) {
  const { formRef } = useIntersectionObserveForms();

  const safetyCategory = catalog.categories.find(
    ({ name }) => name === 'safety',
  );

  const safetyProducts = safetyCategory
    ? catalog.products.filter((p) =>
        p.categories?.find((c) => c.id === safetyCategory.id),
      )
    : [];

  const handleContactUsClick = () => scrollToElementWithId('contact-us');
  const handleServicesClick = () => scrollToElementWithId('catalog');

  return (
    <Layout>
      <div>
        <HeroInnerPage
          breadcrumbs={data.hero.breadcrumbs}
          title={data.hero.title}
          description={data.hero.description}
          heroImage="https://cdn.cloud.ru/backend/images/rko/protection.png"
          buttons={[
            {
              title: data.hero.buttonContactUs.title,
              onClick: handleContactUsClick,
              analyticsOptions: {
                action: 'click',
                clickZone: 'body',
                clickElement: 'button',
                clickContent: data.hero.buttonContactUs.title,
                uniqueId: `security-hero-button-scroll-to-form-${data.hero.buttonContactUs.title}`,
                transitionType: 'inside-link',
              },
            },
            {
              title: data.hero.buttonServices.title,
              variant: Variant.Tertiary,
              onClick: handleServicesClick,
              analyticsOptions: {
                action: 'click',
                clickZone: 'body',
                clickElement: 'button',
                clickContent: data.hero.buttonServices.title,
                uniqueId: `security-hero-button-scroll-to-form-${data.hero.buttonServices.title}`,
                transitionType: 'inside-link',
              },
            },
          ]}
        />
        <Benefits
          title={data.securityLevelsBenefits.title}
          description={data.securityLevelsBenefits.description}
          cards={data.securityLevelsBenefits.cards}
          cardWithShadow
          outline
        />
        <About
          title={data.securityProofs.title}
          description={data.securityProofs.description}
        />
        <Accordion
          className={s.faqItem}
          title={data.diplomas.title}
          cards={data.diplomas.cards}
        />
        <Accordion
          className={s.faqItem}
          title={data.standardsISO.title}
          cards={data.standardsISO.cards}
        />
        <Accordion
          className={s.faqItem}
          title={data.licenses.title}
          cards={data.licenses.cards}
        />
        <Table data={tableData} />
        <div id="catalog">
          <ProductsList
            title={data.productsList.title}
            products={safetyProducts}
            cardsWithShadow
            outline
          />
        </div>
        <FullScreen
          title={data.securityVideo.title}
          videoLink={data.securityVideo.video}
          previewImage={data.securityVideo.preview}
          controls
        />
        <div id="contact-us">
          <Form
            id="form"
            ref={formRef}
            title="Заявка на консультацию"
            inputPath="v2/order"
            additionalFields={{ subsegment: 'consultation' }}
            allFormsSentFormType="consultation"
            analyticsFieldFill={{ formType: 'body' }}
            formVariant="inner"
            successfulSubmissonFormAnalytics={{
              formname: 'consultation',
            }}
          />
        </div>
      </div>
    </Layout>
  );
}
